import { useCallback, useMemo } from 'react';
import { formatCampaignLabelText } from '@/code/frontend/components/campaign/CampaignLabel';
import { useImpersonationActions } from '@/code/frontend/components/employee/common/useImpersonationActions';
import { filterEmployeePageGroups } from '@/code/frontend/components/employee/employeePageRegistry';
import { CommandGroup } from '@/code/frontend/components/ui/command';
import { useDebounce } from '@/code/frontend/hooks/useDebounce';
import { canAccessEmployeePage } from '@/code/lib/employeePageAccess';
import { fullName } from '@/code/lib/utils/initials';
import { trpc } from '@/code/lib/utils/trpc';
import {
  CmdKDialog,
  CmdKLookupCommandItem,
  CmdKLookupHint,
  CmdKLookupResultItem,
  CmdKPageItem,
} from './components';
import {
  EMPLOYEE_LOOKUP_COMMANDS,
  formatUserName,
  getLookupUrl,
  parseLookupCommand,
} from './constants';
import { useCmdKNavigation } from './hooks';
import type { CmdKDialogProps } from './types';

const FIVE_MINUTES = 5 * 60 * 1000;

const LOOKUP_REQUIRED_PAGES: Record<string, readonly string[]> = {
  lead: ['individual-chat'],
  campaign: ['campaigns'],
  leadpool: ['leadpool'],
  user: ['users', 'customers', 'dashboards/low-outreach-users'],
  customer: ['customers'],
  base: [
    'campaign-bases',
    'utils/campaign-base-leads',
    'utils/lead-reignition',
  ],
};

const isUserSearchPrefix = (prefix: string | null) =>
  prefix === 'user' || prefix === 'impersonate';

/**
 * Employee command palette with page navigation and ID lookups.
 */
export function EmployeeCmdK({ open, onOpenChange }: CmdKDialogProps) {
  const {
    query,
    setQuery,
    navigate,
    handleOpenChange,
    trackMetaKey,
    wasModifierPressed,
  } = useCmdKNavigation({ onOpenChange });
  const { openInNewTab, copyLink } = useImpersonationActions();
  const { data: permissions } =
    trpc.auriumEmployee.getEmployeePermissions.useQuery(undefined, {
      enabled: open,
    });

  const pageGroups = useMemo(
    () =>
      filterEmployeePageGroups((slug) =>
        permissions ? canAccessEmployeePage(permissions, slug) : false,
      ),
    [permissions],
  );

  const canUseLookup = useCallback(
    (type: string) => {
      if (!permissions) {
        return false;
      }
      if (permissions.hasFullEmployeeAccess) {
        return true;
      }
      const required = LOOKUP_REQUIRED_PAGES[type];
      if (!required) {
        return false;
      }
      return required.some((slug) => canAccessEmployeePage(permissions, slug));
    },
    [permissions],
  );

  const lookupMatch = useMemo(() => {
    const parsed = parseLookupCommand(query);
    if (parsed?.type === 'impersonate' && !canUseLookup('impersonate')) {
      return null;
    }
    return parsed;
  }, [query, canUseLookup]);

  const currentLookupPrefix = useMemo(() => {
    const lowerQuery = query.toLowerCase();
    const command = EMPLOYEE_LOOKUP_COMMANDS.find((cmd) =>
      lowerQuery.startsWith(cmd.prefix.toLowerCase()),
    );
    const prefix = command
      ? command.prefix.toLowerCase().replace(':', '')
      : null;
    if (prefix === 'impersonate' && !canUseLookup('impersonate')) {
      return null;
    }
    return prefix;
  }, [query, canUseLookup]);

  const lookupSearchTerm = useMemo(() => {
    if (!currentLookupPrefix) return '';
    return query.slice(currentLookupPrefix.length + 1).trimStart();
  }, [query, currentLookupPrefix]);

  const debouncedLookupSearch = useDebounce(lookupSearchTerm, 200);

  const isCampaignLookup =
    currentLookupPrefix === 'campaign' || currentLookupPrefix === 'leadpool';

  const { data: campaignResults } = trpc.employee.searchCampaigns.useQuery(
    { search: debouncedLookupSearch || undefined, limit: 15 },
    {
      enabled:
        open &&
        isCampaignLookup &&
        currentLookupPrefix !== null &&
        canUseLookup(currentLookupPrefix),
      staleTime: FIVE_MINUTES,
    },
  );
  const { data: userResults } = trpc.employee.getUsers.useQuery(
    { search: debouncedLookupSearch || undefined, limit: 15 },
    {
      enabled:
        open &&
        isUserSearchPrefix(currentLookupPrefix) &&
        currentLookupPrefix !== null &&
        canUseLookup(currentLookupPrefix),
      staleTime: FIVE_MINUTES,
    },
  );
  const { data: customerResults } = trpc.employee.searchCustomers.useQuery(
    { search: debouncedLookupSearch || undefined, limit: 15 },
    {
      enabled:
        open && currentLookupPrefix === 'customer' && canUseLookup('customer'),
      staleTime: FIVE_MINUTES,
    },
  );
  const { data: baseResults } = trpc.employee.searchCampaignBases.useQuery(
    { search: debouncedLookupSearch || undefined, limit: 15 },
    {
      enabled: open && currentLookupPrefix === 'base' && canUseLookup('base'),
      staleTime: FIVE_MINUTES,
    },
  );

  const filteredLookupResults = useMemo(() => {
    if (!currentLookupPrefix || currentLookupPrefix === 'lead') return [];

    if (isCampaignLookup && campaignResults) {
      return campaignResults.slice(0, 10).map((c) => ({
        id: c.id,
        name: formatCampaignLabelText({
          name: c.campaignName,
          ownerName: fullName(c.user),
        }),
      }));
    }

    if (isUserSearchPrefix(currentLookupPrefix) && userResults?.data) {
      return userResults.data
        .slice(0, 10)
        .map((u) => ({ id: u.id, name: formatUserName(u) }));
    }

    if (currentLookupPrefix === 'customer' && customerResults) {
      return customerResults.slice(0, 10).map((c) => ({
        id: c.id,
        name: c.name,
      }));
    }

    if (currentLookupPrefix === 'base' && baseResults) {
      return baseResults.slice(0, 10).map((b) => {
        // Disambiguate by organization; standalone bases fall back to owner.
        const scope = b.organizationName ?? b.ownerName;
        return { id: b.id, name: scope ? `${b.name} — ${scope}` : b.name };
      });
    }

    return [];
  }, [
    currentLookupPrefix,
    isCampaignLookup,
    campaignResults,
    userResults,
    customerResults,
    baseResults,
  ]);

  const allowedLookupCommands = useMemo(
    () =>
      EMPLOYEE_LOOKUP_COMMANDS.filter((cmd) =>
        canUseLookup(cmd.prefix.replace(/:$/, '')),
      ),
    [canUseLookup],
  );

  const filteredGroups = useMemo(() => {
    if (!query.trim()) return pageGroups;
    const lowerQuery = query.toLowerCase();

    if (
      allowedLookupCommands.some((cmd) =>
        lowerQuery.startsWith(cmd.prefix.toLowerCase()),
      )
    ) {
      return [];
    }

    return pageGroups
      .map((group) => {
        if (group.heading.toLowerCase().includes(lowerQuery)) {
          return group;
        }
        return {
          ...group,
          items: group.items.filter(
            (item) =>
              item.label.toLowerCase().includes(lowerQuery) ||
              item.keywords?.some((kw) =>
                kw.toLowerCase().includes(lowerQuery),
              ),
          ),
        };
      })
      .filter((group) => group.items.length > 0);
  }, [query, pageGroups, allowedLookupCommands]);

  const filteredLookupCommands = useMemo(() => {
    if (!query.trim()) return allowedLookupCommands;
    if (lookupMatch) return [];
    const lowerQuery = query.toLowerCase();
    return allowedLookupCommands.filter((cmd) =>
      cmd.prefix.toLowerCase().startsWith(lowerQuery),
    );
  }, [query, lookupMatch, allowedLookupCommands]);

  const handleLookupSelect = useCallback(
    (prefix: string) => {
      setQuery(prefix);
    },
    [setQuery],
  );

  const runImpersonation = useCallback(
    async (userId: string) => {
      if (!canUseLookup('impersonate')) return;
      const copy = wasModifierPressed();
      handleOpenChange(false);
      if (copy) {
        await copyLink(userId);
      } else {
        await openInNewTab(userId);
      }
    },
    [
      canUseLookup,
      copyLink,
      handleOpenChange,
      openInNewTab,
      wasModifierPressed,
    ],
  );

  const handleResultSelect = useCallback(
    async (type: string, id: string) => {
      if (type === 'impersonate') {
        await runImpersonation(id);
        return;
      }
      const url = getLookupUrl(type, id);
      if (url) navigate(url);
    },
    [navigate, runImpersonation],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      trackMetaKey(e);

      const firstCommand = filteredLookupCommands[0];
      if (e.key === 'Tab' && firstCommand && !lookupMatch) {
        e.preventDefault();
        setQuery(firstCommand.prefix);
      }

      // Only use direct lookupMatch navigation when no filtered results are showing.
      // This handles cases where an exact ID is typed that isn't in the visible results.
      // When results are displayed, let cmdk handle Enter to select the highlighted item.
      if (
        e.key === 'Enter' &&
        lookupMatch?.id &&
        filteredLookupResults.length === 0 &&
        lookupMatch.type !== 'leadpool' &&
        lookupMatch.type !== 'impersonate' &&
        (lookupMatch.type !== 'customer' ||
          /^(org:|user:)/i.test(lookupMatch.id) ||
          /^c[a-z0-9]{20,}$/i.test(lookupMatch.id))
      ) {
        e.preventDefault();
        const url = getLookupUrl(lookupMatch.type, lookupMatch.id);
        if (url) navigate(url);
      }
    },
    [
      filteredLookupCommands,
      filteredLookupResults.length,
      lookupMatch,
      navigate,
      setQuery,
      trackMetaKey,
    ],
  );

  const hasNoResults =
    filteredGroups.length === 0 &&
    filteredLookupCommands.length === 0 &&
    !lookupMatch &&
    filteredLookupResults.length === 0 &&
    query.trim().length > 0;

  const hintType =
    currentLookupPrefix === 'impersonate' ? 'impersonate' : lookupMatch?.type;
  const hintId =
    currentLookupPrefix === 'impersonate' ? lookupSearchTerm : lookupMatch?.id;

  return (
    <CmdKDialog
      open={open}
      onOpenChange={handleOpenChange}
      placeholder="Search pages or use lead:, campaign:, leadpool:, user:, customer:, base:, impersonate: to lookup..."
      query={query}
      onQueryChange={setQuery}
      onKeyDown={handleKeyDown}
      onKeyUp={trackMetaKey}
      onPointerDown={trackMetaKey}
      emptyMessage={hasNoResults ? 'No results found.' : undefined}
    >
      {hintType && <CmdKLookupHint type={hintType} id={hintId ?? ''} />}

      {currentLookupPrefix && filteredLookupResults.length > 0 && (
        <CommandGroup heading="Results">
          {filteredLookupResults.map((item) => (
            <CmdKLookupResultItem
              key={item.id}
              lookupType={currentLookupPrefix}
              id={item.id}
              name={item.name}
              onSelect={async () => {
                await handleResultSelect(currentLookupPrefix, item.id);
              }}
            />
          ))}
        </CommandGroup>
      )}

      {filteredLookupCommands.length > 0 && !lookupMatch && (
        <CommandGroup heading="Quick Lookup">
          {filteredLookupCommands.map((cmd) => (
            <CmdKLookupCommandItem
              key={cmd.prefix}
              command={cmd}
              onSelect={() => handleLookupSelect(cmd.prefix)}
            />
          ))}
        </CommandGroup>
      )}

      {filteredGroups.map((group) => (
        <CommandGroup key={group.heading} heading={group.heading}>
          {group.items.map((page) => (
            <CmdKPageItem key={page.href} page={page} onSelect={navigate} />
          ))}
        </CommandGroup>
      ))}
    </CmdKDialog>
  );
}
